import { useEffect, useMemo, useRef, useState } from "react";
import { api, extractError, API } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  Video as VideoIcon, Plus, Play, Trash2, Pencil, Eye, EyeOff,
  Bus, Puzzle, Target, Trophy, Upload, Search, X, Link as LinkIcon
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { id: "trips",  label: "رحلات",           icon: Bus,    color: "from-amber-500 to-orange-600",  bg: "bg-amber-50",   text: "text-amber-700",   border: "border-amber-200" },
  { id: "rehab",  label: "تأهيل",           icon: Puzzle, color: "from-sky-500 to-blue-600",       bg: "bg-sky-50",     text: "text-sky-700",     border: "border-sky-200" },
  { id: "skills", label: "تنمية مهارات",    icon: Target, color: "from-violet-500 to-purple-600",  bg: "bg-violet-50",  text: "text-violet-700",  border: "border-violet-200" },
  { id: "sports", label: "رياضة",           icon: Trophy, color: "from-emerald-500 to-teal-600",   bg: "bg-emerald-50", text: "text-emerald-700", border: "border-emerald-200" },
];

const emptyForm = {
  title: "", description: "", category: "rehab",
  source_type: "youtube", video_url: "", storage_path: "", thumbnail_url: "",
  published: true,
};

function videoStreamUrl(v) {
  if (v.source_type === "upload" && v.storage_path) {
    return `${API}/files/${v.storage_path}`;
  }
  return v.embed_url || v.video_url || "";
}

export default function VideosPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [player, setPlayer] = useState(null);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const { data } = await api.get("/videos");
      setItems(data);
    } catch (e) {
      toast.error(extractError(e));
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((v) => {
      if (filter !== "all" && v.category !== filter) return false;
      if (!q) return true;
      return (v.title || "").toLowerCase().includes(q) || (v.description || "").toLowerCase().includes(q);
    });
  }, [items, filter, search]);

  const counts = useMemo(() => {
    const c = { all: items.length };
    CATEGORIES.forEach((cat) => { c[cat.id] = items.filter((v) => v.category === cat.id).length; });
    return c;
  }, [items]);

  const openNew = () => { setEditing(null); setForm(emptyForm); setOpen(true); };
  const openEdit = (v) => {
    setEditing(v);
    setForm({
      title: v.title || "",
      description: v.description || "",
      category: v.category || "rehab",
      source_type: v.source_type || "youtube",
      video_url: v.video_url || "",
      storage_path: v.storage_path || "",
      thumbnail_url: v.thumbnail_url || "",
      published: !!v.published,
    });
    setOpen(true);
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("العنوان مطلوب"); return; }
    if (form.source_type === "upload" && !form.storage_path) {
      toast.error("يرجى رفع ملف الفيديو أولاً"); return;
    }
    if (form.source_type !== "upload" && !form.video_url.trim()) {
      toast.error("رابط الفيديو مطلوب"); return;
    }
    try {
      if (editing) await api.patch(`/videos/${editing.id}`, form);
      else await api.post("/videos", form);
      toast.success(editing ? "تم التحديث" : "تمت الإضافة");
      setOpen(false); setForm(emptyForm); setEditing(null);
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const del = async (v) => {
    if (!confirm(`حذف الفيديو "${v.title}" نهائياً؟`)) return;
    try { await api.delete(`/videos/${v.id}`); toast.success("تم الحذف"); load(); }
    catch (e) { toast.error(extractError(e)); }
  };

  const togglePublish = async (v) => {
    try {
      await api.patch(`/videos/${v.id}`, { ...v, published: !v.published });
      toast.success(v.published ? "تم إلغاء النشر" : "تم النشر");
      load();
    } catch (e) { toast.error(extractError(e)); }
  };

  const onFilePick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 200 * 1024 * 1024) { toast.error("حجم الفيديو يتجاوز 200MB"); return; }
    const fd = new FormData();
    fd.append("file", f);
    setUploading(true);
    try {
      const { data } = await api.post("/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((s) => ({ ...s, storage_path: data.storage_path, source_type: "upload" }));
      toast.success("تم رفع الفيديو بنجاح");
    } catch (err) {
      toast.error(extractError(err));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="space-y-6" data-testid="videos-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black text-slate-900 flex items-center gap-3">
            <VideoIcon className="w-8 h-8 text-rose-600" />
            مكتبة الفيديوهات التعليمية
          </h1>
          <p className="text-slate-500 mt-2">مقاطع مختارة للأسر ومقدمي الرعاية</p>
        </div>
        {isAdmin && (
          <Button onClick={openNew} className="h-12 gap-2 bg-gradient-to-l from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 shadow-lg" data-testid="new-video-btn">
            <Plus className="w-4 h-4" /> فيديو جديد
          </Button>
        )}
      </div>

      {/* Filter tabs */}
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("all")}
          className={`px-4 py-2 rounded-full font-bold text-sm transition-all ${filter === "all" ? "bg-slate-900 text-white shadow-md" : "bg-white border border-slate-200 text-slate-700 hover:border-slate-400"}`}
          data-testid="cat-filter-all"
        >
          الكل <span className="opacity-70">({counts.all})</span>
        </button>
        {CATEGORIES.map((c) => (
          <button
            key={c.id}
            onClick={() => setFilter(c.id)}
            className={`px-4 py-2 rounded-full font-bold text-sm gap-2 inline-flex items-center transition-all ${filter === c.id ? `bg-gradient-to-l ${c.color} text-white shadow-md` : `bg-white border ${c.border} ${c.text} hover:brightness-95`}`}
            data-testid={`cat-filter-${c.id}`}
          >
            <c.icon className="w-4 h-4" /> {c.label} <span className="opacity-80">({counts[c.id] || 0})</span>
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="بحث بالعنوان أو الوصف..."
          className="pr-10"
          data-testid="video-search"
        />
      </div>

      {/* Grid */}
      {filtered.length === 0 ? (
        <Card><CardContent className="p-16 text-center text-slate-400">
          <VideoIcon className="w-14 h-14 mx-auto mb-3 opacity-40" />
          <div className="font-bold">لا توجد فيديوهات في هذا التصنيف</div>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((v) => {
            const cat = CATEGORIES.find((c) => c.id === v.category) || CATEGORIES[1];
            return (
              <Card key={v.id} className="overflow-hidden border-slate-200 hover:shadow-xl transition-all group" data-testid={`video-card-${v.id}`}>
                <button
                  onClick={() => setPlayer(v)}
                  className="relative w-full aspect-video bg-slate-900 overflow-hidden block"
                  data-testid={`play-video-${v.id}`}
                >
                  {v.thumbnail_url ? (
                    <img src={v.thumbnail_url} alt={v.title} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 group-hover:scale-105 transition-all" />
                  ) : (
                    <div className={`w-full h-full flex items-center justify-center bg-gradient-to-br ${cat.color}`}>
                      <VideoIcon className="w-16 h-16 text-white/70" />
                    </div>
                  )}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/30 transition-colors">
                    <div className="w-16 h-16 rounded-full bg-white/95 shadow-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                      <Play className="w-8 h-8 text-rose-600 mr-1" fill="currentColor" />
                    </div>
                  </div>
                  <div className={`absolute top-3 right-3 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/90 backdrop-blur ${cat.text} text-xs font-black shadow`}>
                    <cat.icon className="w-3 h-3" /> {cat.label}
                  </div>
                  {isAdmin && !v.published && (
                    <div className="absolute top-3 left-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-900/80 text-white text-[10px] font-black">
                      <EyeOff className="w-3 h-3" /> مسودة
                    </div>
                  )}
                </button>
                <CardContent className="p-4">
                  <h3 className="font-black text-slate-900 line-clamp-2 leading-tight">{v.title}</h3>
                  {v.description && <p className="mt-1.5 text-sm text-slate-600 line-clamp-2">{v.description}</p>}
                  <div className="mt-3 flex items-center justify-between text-[11px] text-slate-400">
                    <span>{v.author_name || ""}</span>
                    <span>{(v.created_at || "").slice(0, 10)}</span>
                  </div>
                  {isAdmin && (
                    <div className="mt-3 pt-3 border-t border-slate-100 flex flex-wrap gap-2">
                      <Button variant="outline" size="sm" onClick={() => openEdit(v)} className="gap-1.5" data-testid={`edit-video-${v.id}`}>
                        <Pencil className="w-3.5 h-3.5" /> تعديل
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => togglePublish(v)}
                        className={`gap-1.5 ${v.published ? "text-amber-700 border-amber-200" : "text-emerald-700 border-emerald-200"}`}
                        data-testid={`toggle-publish-${v.id}`}
                      >
                        {v.published ? <><EyeOff className="w-3.5 h-3.5" /> إلغاء</> : <><Eye className="w-3.5 h-3.5" /> نشر</>}
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => del(v)} className="gap-1.5 text-rose-700 border-rose-200" data-testid={`delete-video-${v.id}`}>
                        <Trash2 className="w-3.5 h-3.5" /> حذف
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Player modal */}
      <Dialog open={!!player} onOpenChange={(o) => !o && setPlayer(null)}>
        <DialogContent className="max-w-4xl p-0 overflow-hidden bg-slate-900 border-0" data-testid="video-player">
          <DialogTitle className="sr-only">{player?.title || "مشغل الفيديو"}</DialogTitle>
          {player && (
            <>
              <div className="aspect-video bg-black">
                {player.source_type === "upload" ? (
                  <video src={videoStreamUrl(player)} controls autoPlay className="w-full h-full" />
                ) : (
                  <iframe
                    src={videoStreamUrl(player)}
                    title={player.title}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                    className="w-full h-full"
                  />
                )}
              </div>
              <div className="p-5 bg-white">
                <h3 className="font-black text-slate-900 text-lg">{player.title}</h3>
                {player.description && <p className="mt-2 text-slate-600 text-sm leading-relaxed">{player.description}</p>}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Add/Edit dialog */}
      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="video-dialog">
            <DialogHeader>
              <DialogTitle className="text-2xl font-black">{editing ? "تعديل فيديو" : "فيديو جديد"}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-1.5">
                <Label className="font-bold">العنوان *</Label>
                <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="v-title" />
              </div>

              <div className="space-y-1.5">
                <Label className="font-bold">التصنيف</Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {CATEGORIES.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => setForm({ ...form, category: c.id })}
                      className={`p-3 rounded-2xl border-2 text-sm font-bold transition-all flex items-center gap-2 justify-center ${form.category === c.id ? `bg-gradient-to-l ${c.color} text-white border-transparent` : `bg-white ${c.border} ${c.text}`}`}
                      data-testid={`v-cat-${c.id}`}
                    >
                      <c.icon className="w-4 h-4" /> {c.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="font-bold">مصدر الفيديو</Label>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    { id: "youtube", label: "YouTube", icon: LinkIcon },
                    { id: "vimeo",   label: "Vimeo",   icon: LinkIcon },
                    { id: "upload",  label: "رفع ملف", icon: Upload },
                  ].map((s) => (
                    <button
                      key={s.id}
                      type="button"
                      onClick={() => setForm({ ...form, source_type: s.id, video_url: s.id === "upload" ? "" : form.video_url })}
                      className={`p-3 rounded-2xl border-2 text-sm font-bold transition-all flex items-center gap-2 justify-center ${form.source_type === s.id ? "bg-slate-900 text-white border-transparent" : "bg-white border-slate-200 text-slate-700"}`}
                      data-testid={`v-src-${s.id}`}
                    >
                      <s.icon className="w-4 h-4" /> {s.label}
                    </button>
                  ))}
                </div>
              </div>

              {form.source_type !== "upload" ? (
                <div className="space-y-1.5">
                  <Label className="font-bold">رابط الفيديو *</Label>
                  <Input
                    dir="ltr"
                    type="url"
                    value={form.video_url}
                    onChange={(e) => setForm({ ...form, video_url: e.target.value })}
                    placeholder={form.source_type === "vimeo" ? "https://vimeo.com/123456789" : "https://www.youtube.com/watch?v=..."}
                    data-testid="v-url"
                  />
                  <p className="text-xs text-slate-500">
                    {form.source_type === "youtube" ? "أي رابط يوتيوب (watch, youtu.be, shorts)" : "رابط Vimeo قياسي"}
                  </p>
                </div>
              ) : (
                <div className="space-y-1.5">
                  <Label className="font-bold">ملف الفيديو (mp4 / webm / mov — حتى 200MB)</Label>
                  <div className="flex gap-2 items-center">
                    <input
                      ref={fileRef}
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      onChange={onFilePick}
                      className="hidden"
                      data-testid="v-file-input"
                    />
                    <Button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      variant="outline"
                      className="gap-2"
                      data-testid="v-file-btn"
                    >
                      <Upload className="w-4 h-4" /> {uploading ? "جاري الرفع..." : "اختر ملفاً"}
                    </Button>
                    {form.storage_path && (
                      <div className="text-xs text-emerald-700 font-bold inline-flex items-center gap-1">
                        <Play className="w-3 h-3" /> تم الرفع
                        <button type="button" onClick={() => setForm({ ...form, storage_path: "" })} className="text-rose-600" data-testid="v-clear-upload">
                          <X className="w-3 h-3" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              <div className="space-y-1.5">
                <Label className="font-bold">صورة مصغرة (اختياري)</Label>
                <Input
                  dir="ltr"
                  type="url"
                  value={form.thumbnail_url}
                  onChange={(e) => setForm({ ...form, thumbnail_url: e.target.value })}
                  placeholder="https://... (تُستخرج تلقائياً من يوتيوب)"
                  data-testid="v-thumb"
                />
              </div>

              <div className="space-y-1.5">
                <Label className="font-bold">الوصف</Label>
                <Textarea
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  rows={4}
                  placeholder="لمحة عن الفيديو ومحتواه..."
                  data-testid="v-desc"
                />
              </div>

              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                <div>
                  <div className="font-bold text-slate-800">النشر الفوري</div>
                  <div className="text-xs text-slate-500">سيظهر للمعلمين وأولياء الأمور مباشرة</div>
                </div>
                <Switch checked={form.published} onCheckedChange={(v) => setForm({ ...form, published: v })} data-testid="v-published" />
              </div>

              <Button onClick={save} className="w-full h-12 bg-gradient-to-l from-rose-600 to-pink-600 hover:from-rose-700 hover:to-pink-700 gap-2" data-testid="save-video-btn">
                <VideoIcon className="w-4 h-4" /> {editing ? "تحديث" : "إضافة"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
